import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell, { Field, Input, AuthLink } from "@/saas/components/AuthShell";
import SButton from "@/saas/components/SButton";
import TermsModal from "@/saas/components/TermsModal";
import { TERMS_VERSION } from "@/saas/legal/terms";
import { useI18n } from "@/saas/i18n";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

export default function Register() {
  const { t } = useI18n();
  const { register } = useSaasAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Phase 9.1 — Legal acceptance at signup (was previously only at checkout).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // Phase 10 — KVKK uyumlu pazarlama opt-in (default off; explicit acceptance only).
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Şifre en az 8 karakter olmalı.");
      return;
    }
    if (password !== confirm) {
      setErr(t("auth.field.confirm") + " ≠ " + t("auth.field.password"));
      return;
    }
    if (!termsAccepted) {
      setErr("Devam etmek için Kullanım Şartlarını ve İptal Politikasını onaylamanız gerekiyor.");
      return;
    }
    setLoading(true);
    const res = await register({
      full_name: name,
      email, password,
      terms_accepted:    true,
      terms_version:     TERMS_VERSION,
      marketing_opt_in:  marketingOptIn,
    });
    setLoading(false);
    if (!res.ok) { setErr(res.error); return; }
    navigate("/plans", { replace: true });
  };

  return (
    <AuthShell
      title={t("auth.register.title")}
      subtitle={t("auth.register.subtitle")}
      footer={
        <p className="text-sm text-white/60">
          {t("auth.has_account")} <AuthLink to="/login">{t("nav.login")}</AuthLink>
        </p>
      }
    >
      <form className="space-y-5" onSubmit={submit} data-testid="register-form">
        <Field label={t("auth.field.name")}>
          <Input
            type="text" required autoComplete="name"
            value={name} onChange={(e) => setName(e.target.value)}
            data-testid="register-name"
          />
        </Field>
        <Field label={t("auth.field.email")}>
          <Input
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("auth.field.password")}>
            <Input
              type="password" required autoComplete="new-password" minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password"
            />
          </Field>
          <Field label={t("auth.field.confirm")}>
            <Input
              type="password" required autoComplete="new-password" minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="register-confirm"
            />
          </Field>
        </div>

        {/* Phase 9.1 — Legal checkbox */}
        <label
          htmlFor="register-terms"
          className="flex items-start gap-3 cursor-pointer select-none"
          data-testid="register-terms-row"
        >
          <input
            id="register-terms"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setErr(""); }}
            className="mt-0.5 w-4 h-4 shrink-0 accent-gold cursor-pointer"
            data-testid="register-terms-checkbox"
          />
          <span className="text-xs text-white/75 leading-relaxed">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
              className="underline decoration-gold/60 underline-offset-4 hover:text-gold transition-colors"
              data-testid="register-terms-link"
            >
              Kullanım Şartlarını ve İptal Politikasını
            </button>{" "}
            okudum, kabul ediyorum.
            <span className="ml-1 text-[10px] font-mono text-white/35">(sürüm {TERMS_VERSION})</span>
          </span>
        </label>

        {/* Phase 10 — Marketing opt-in (KVKK / GDPR — default OFF) */}
        <label
          htmlFor="register-marketing"
          className="flex items-start gap-3 cursor-pointer select-none -mt-2"
          data-testid="register-marketing-row"
        >
          <input
            id="register-marketing"
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 accent-gold cursor-pointer"
            data-testid="register-marketing-checkbox"
          />
          <span className="text-xs text-white/65 leading-relaxed">
            Yeni içerikler ve özel indirimlerden e-posta ile haberdar olmak istiyorum.
            <span className="ml-1 text-[10px] font-mono text-white/35 uppercase tracking-widest">opsiyonel · KVKK uyumlu</span>
          </span>
        </label>

        {err && <p className="text-xs text-err -mt-2" data-testid="register-error">{err}</p>}
        <SButton
          type="submit" size="lg" className="w-full"
          disabled={loading || !termsAccepted}
          data-testid="register-submit"
        >
          {loading ? t("common.loading") : t("auth.submit.register")}
        </SButton>
      </form>

      <TermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        onAccept={() => setTermsAccepted(true)}
      />
    </AuthShell>
  );
}
